import { z } from 'zod';
export const AiContextTypeSchema = z.enum(['NONE', 'CASE', 'CLIENT', 'DOCUMENT']);
export const AiContextTypeLabel = {
    NONE: 'Sin contexto',
    CASE: 'Causa',
    CLIENT: 'Cliente',
    DOCUMENT: 'Documento',
};
export const AiMessageRoleSchema = z.enum(['USER', 'ASSISTANT']);
export const AiMessageDtoSchema = z.object({
    id: z.string(),
    role: AiMessageRoleSchema,
    content: z.string(),
    createdAt: z.string().datetime(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheCreationInputTokens: z.number().int().nonnegative(),
    cacheReadInputTokens: z.number().int().nonnegative(),
});
export const AiConversationListItemSchema = z.object({
    id: z.string(),
    title: z.string(),
    model: z.string(),
    contextType: AiContextTypeSchema,
    contextId: z.string().nullable(),
    contextLabel: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    lastMessagePreview: z.string().nullable(),
});
export const AiConversationDetailSchema = AiConversationListItemSchema.extend({
    messages: z.array(AiMessageDtoSchema),
});
export const AiConversationListResponseSchema = z.object({
    items: z.array(AiConversationListItemSchema),
});
// ---------- Inputs ----------
export const AiChatRequestSchema = z.object({
    /** Si se omite, se crea una nueva conversación. */
    conversationId: z.string().optional(),
    /** Texto del mensaje del usuario. */
    message: z.string().min(1).max(8000),
    /** Tipo de contexto a inyectar (solo se usa al crear conversación nueva). */
    contextType: AiContextTypeSchema.optional(),
    /** ID del contexto (causaId/clienteId/documentId). */
    contextId: z.string().optional(),
});
export const AiCreateConversationSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    contextType: AiContextTypeSchema.default('NONE'),
    contextId: z.string().nullable().optional(),
});
export const AiUpdateConversationSchema = z.object({
    title: z.string().min(1).max(200).optional(),
});
// ---------- Acciones rápidas ----------
export const AiSummaryRequestSchema = z.object({
    contextType: z.enum(['CASE', 'CLIENT', 'DOCUMENT']),
    contextId: z.string(),
    /** Estilo del resumen. */
    style: z.enum(['BRIEF', 'DETAILED', 'BULLETS']).default('BRIEF'),
});
export const AiSummaryResponseSchema = z.object({
    text: z.string(),
    conversationId: z.string(),
});
export const AiDraftRequestSchema = z.object({
    /** Causa de referencia (opcional, pero recomendado para contexto). */
    caseId: z.string().optional(),
    /** Tipo de escrito a redactar. */
    kind: z.enum([
        'DEMANDA',
        'CONTESTACION',
        'RECURSO',
        'ESCRITO',
        'CORREO_CLIENTE',
        'CORREO_PROCURADOR',
        'OTRO',
    ]),
    /** Instrucciones libres del abogado. */
    instructions: z.string().min(5).max(4000),
});
export const AiDraftResponseSchema = z.object({
    text: z.string(),
    conversationId: z.string(),
});
export const AiStatusResponseSchema = z.object({
    available: z.boolean(),
    model: z.string(),
    ragEnabled: z.boolean(),
    embeddingModel: z.string().nullable(),
});
// ---------- RAG ----------
export const RagSearchQuerySchema = z.object({
    q: z.string().min(2).max(500),
    caseId: z.string().optional(),
    topK: z.coerce.number().int().min(1).max(20).default(6),
});
export const RagHitSchema = z.object({
    documentId: z.string(),
    chunkId: z.string(),
    chunkIndex: z.number().int().nonnegative(),
    documentName: z.string(),
    caseId: z.string().nullable(),
    caseCode: z.string().nullable(),
    caseTitle: z.string().nullable(),
    pageStart: z.number().int().nullable(),
    pageEnd: z.number().int().nullable(),
    content: z.string(),
    /** Distancia coseno (0=idéntico). Más bajo = más relevante. */
    distance: z.number(),
});
export const RagSearchResponseSchema = z.object({
    query: z.string(),
    hits: z.array(RagHitSchema),
});
/** Una referencia [REF#] emitida en el evento done del stream de chat. */
export const ChatReferenceSchema = z.object({
    ref: z.string(), // "REF1", "REF2", …
    documentId: z.string(),
    chunkId: z.string(),
    documentName: z.string(),
    pageStart: z.number().int().nullable(),
    pageEnd: z.number().int().nullable(),
    caseId: z.string().nullable(),
    /** Snippet del chunk citado (recortado a ~600 chars). */
    snippet: z.string(),
});
// ---------- Indexación documento (estado para UI) ----------
export const DocumentIndexStatusSchema = z.enum([
    'PENDING',
    'PROCESSING',
    'DONE',
    'FAILED',
    'SKIPPED',
]);
export const DocumentIndexStatusLabel = {
    PENDING: 'Pendiente',
    PROCESSING: 'Indexando…',
    DONE: 'Indexado',
    FAILED: 'Fallo',
    SKIPPED: 'No indexable',
};
export const DocumentExtractedResponseSchema = z.object({
    id: z.string(),
    fileName: z.string(),
    text: z.string(),
    pageCount: z.number().int().nullable(),
    indexStatus: DocumentIndexStatusSchema,
    indexedAt: z.string().datetime().nullable(),
    indexError: z.string().nullable(),
});
//# sourceMappingURL=ai.js.map